import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiTrendingUp, FiClock } = FiIcons;

const AnimatedCounter = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const animate = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}</span>;
};

const Stats = () => {
  const stats = [
    {
      icon: FiAward,
      value: 13,
      suffix: '+',
      label: 'Years Experience',
      color: 'text-blue-400'
    },
    {
      icon: FiTrendingUp,
      value: 500,
      suffix: '+',
      label: 'Projects Completed',
      color: 'text-cyan-400'
    },
    {
      icon: FiUsers,
      value: 200,
      suffix: '+',
      label: 'Happy Clients',
      color: 'text-green-400'
    },
    {
      icon: FiClock,
      value: 24,
      suffix: '/7',
      label: 'Support Available',
      color: 'text-purple-400'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Trusted by Many</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Our track record speaks for itself. See why customers choose us for their electrical and automation needs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <div className="relative inline-block mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 glow-blue">
                  <SafeIcon 
                    icon={stat.icon} 
                    className={`text-3xl ${stat.color}`} 
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="text-4xl md:text-5xl font-bold text-white">
                  <AnimatedCounter end={stat.value} />
                  <span className={stat.color}>{stat.suffix}</span>
                </div>
                <p className="text-gray-400 font-medium">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;